import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  CssBaseline,
  Avatar,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Construction,
  Message,
  Build,
  Logout,
  Dashboard as DashboardIcon,
  Person,
  Menu as MenuIcon
} from '@mui/icons-material';
import { adminLogout } from '../utils/api';
import AdminFooter from './AdminFooter';

const drawerWidth = 280;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile' })(
  ({ theme, open, isMobile }) => ({
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: isMobile ? 0 : `-${drawerWidth}px`,
    ...(open && !isMobile && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'center',
}));

const AdminLayout = ({ children, title = 'Admin Panel' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // md = 900px
  const [adminInfo, setAdminInfo] = useState(null);
  const [open, setOpen] = useState(!isMobile);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    // Redirect if not logged in
    const isLoggedIn = localStorage.getItem('adminLoggedIn');
    if (!isLoggedIn) {
      navigate('/admin/login');
      return;
    }
    // Load admin info
    const storedAdminInfo = localStorage.getItem('adminInfo');
    if (storedAdminInfo) {
      setAdminInfo(JSON.parse(storedAdminInfo));
    }
  }, [navigate]);

  useEffect(() => {
    // Update drawer state when screen size changes
    setOpen(!isMobile);
  }, [isMobile]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setOpen(!open);
    }
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
    } catch (error) {
      // ignore
    } finally {
      localStorage.removeItem('adminLoggedIn');
      localStorage.removeItem('adminInfo');
      navigate('/admin/login');
    }
  };

  const handleViewWebsite = async () => {
    try {
      await adminLogout();
    } catch (error) {
      // ignore
    } finally {
      localStorage.removeItem('adminLoggedIn');
      localStorage.removeItem('adminInfo');
      navigate('/');
    }
  };

  const sidebarItems = [
    { title: 'Overview', icon: DashboardIcon, path: '/admin/dashboard' },
    { title: 'Manage Projects', icon: Construction, path: '/admin/projects' },
    { title: 'View Messages', icon: Message, path: '/admin/messages' },
    { title: 'Manage Services', icon: Build, path: '/admin/services' },
    { title: 'Profile', icon: Person, path: '/admin/profile' }
  ];

  const getCurrentPath = () => location.pathname;

  // Drawer content
  const drawerContent = (
    <>
      <DrawerHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
          <DashboardIcon sx={{ mr: 1, color: 'primary.main', fontSize: 30 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Admin Panel
          </Typography>
        </Box>
      </DrawerHeader>
      <Divider />
      <List sx={{ px: 1 }}>
        {sidebarItems.map((item) => {
          const IconComponent = item.icon;
          const isActive = getCurrentPath() === item.path;
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  minHeight: 56,
                  backgroundColor: isActive ? 'primary.main' : 'transparent',
                  color: isActive ? 'white' : 'text.primary',
                  '&:hover': {
                    backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                  }
                }}
              >
                <ListItemIcon sx={{ color: isActive ? 'white' : 'primary.main', minWidth: 40 }}>
                  <IconComponent />
                </ListItemIcon>
                <ListItemText primary={item.title} sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.95rem'
                  }
                }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${open && !isMobile ? drawerWidth : 0}px)`,
          ml: open && !isMobile ? `${drawerWidth}px` : 0,
          transition: (theme) =>
            theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <Toolbar>
          {isMobile && (
            <Button color="inherit" onClick={handleDrawerToggle} sx={{ mr: 2, minWidth: 'auto' }}>
              <MenuIcon />
            </Button>
          )}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Dashboard
          </Typography>
          <Button color="inherit" onClick={handleViewWebsite} sx={{ mr: 1,
            fontSize: { xs: '0.5rem', sm: '0.9rem', md: '1rem' } }}
           >
            View Website
          </Button>
          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={<Logout />}
            variant="outlined"
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.5)',
              fontSize: { xs: '0.7rem', sm: '0.9rem', md: '1rem' },
              '&:hover': {
                borderColor: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={isMobile ? mobileOpen : open}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: '#f8f9fa',
            borderRight: '1px solid #e0e0e0',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Main open={open} isMobile={isMobile}>
        <DrawerHeader />
        <Box sx={{ flexGrow: 1, pb: 2 }}>
          {children}
        </Box>
        <AdminFooter />
      </Main>
    </Box>
  );
};

export default AdminLayout;
